/**
 * #28 — wrapped multi-line legend geometry for the time-series chart.
 *
 * Replaces the previous `legend.type: 'scroll'` pager. The contract is that *every* legend
 * entry stays present and visible: entries wrap onto as many rows as they need, the bottom
 * grid reservation grows to match, and the chart canvas grows by the same amount so the
 * plot area is never squeezed.
 *
 * Deliberately pure and DOM-free (the caller injects a `TextMeasurer`) so the row packing,
 * truncation and height arithmetic are unit-testable without a chart instance.
 *
 * The geometry constants below must stay in step with the ECharts `legend` options built
 * from this result — the measurement model and the renderer have to agree on font, icon
 * width, gaps and row height or the reserved height will not match what ECharts draws.
 */

/** How a legend row is measured. Mirrors the ECharts `legend` options one-for-one. */
export interface LegendGeometry {
  /** `legend.textStyle.fontSize` */
  fontSizePx: number;
  /** `legend.textStyle.fontFamily` */
  fontFamily: string;
  /** `legend.itemWidth` — the coloured icon in front of each label. */
  iconWidthPx: number;
  /** Gap ECharts leaves between an item's icon and its text. */
  iconTextGapPx: number;
  /** `legend.itemGap` — horizontal gap between adjacent items on a row. */
  itemGapPx: number;
  /** `legend.itemHeight` — icon height; the text box is the taller of the two. */
  iconHeightPx: number;
  /** Vertical advance of one wrapped legend row (item height + inter-row gap). */
  rowHeightPx: number;
  /** Horizontal padding reserved either side of the legend block. */
  sidePaddingPx: number;
  /**
   * Ceiling on a single label's width, as a fraction of the usable legend width. Guards
   * against one pathological label consuming an entire row; it never removes an entry and
   * never changes the underlying `series.name`.
   */
  maxLabelFractionOfWidth: number;
  /** Appended to a label shortened by the width guard. */
  ellipsis: string;
}

/** Chart-level geometry derived from the legend row count. */
export interface ChartGeometry {
  /**
   * Vertical band reserved for the x-axis date labels, below the plot and above the
   * legend. `X_AXIS_LABEL_BAND_PX + rowHeightPx` reproduces the previous fixed
   * `LEGEND_GRID_BOTTOM_PX = 70` exactly for a one-row legend.
   */
  xAxisLabelBandPx: number;
  /** Canvas height for a one-row legend — today's effective height, kept identical. */
  baseChartHeightPx: number;
  /** Lower bound on the usable legend width, so a collapsed container cannot explode the row count. */
  minLegendWidthPx: number;
}

export const DEFAULT_LEGEND_GEOMETRY: LegendGeometry = {
  fontSizePx: 12,
  fontFamily: 'sans-serif',
  iconWidthPx: 25,
  iconTextGapPx: 5,
  itemGapPx: 10,
  iconHeightPx: 14,
  rowHeightPx: 28,
  sidePaddingPx: 20,
  maxLabelFractionOfWidth: 0.4,
  ellipsis: '…',
};

export const DEFAULT_CHART_GEOMETRY: ChartGeometry = {
  xAxisLabelBandPx: 42,
  baseChartHeightPx: 400,
  minLegendWidthPx: 120,
};

/** Measures the rendered width of a string, in CSS pixels. */
export type TextMeasurer = (text: string) => number;

export interface TimeSeriesChartLayout {
  /** Display label per input name, in input order. Truncated only by the width guard. */
  displayLabels: string[];
  /** Full `series.name` -> display label. Feeds the ECharts `legend.formatter`. */
  displayByName: Record<string, string>;
  /** Display labels grouped into the rows they wrap onto. Every entry appears exactly once. */
  rows: string[][];
  rowCount: number;
  /** Usable legend width — fed to `legend.width` so ECharts wraps where we measured. */
  legendWidthPx: number;
  legendHeightPx: number;
  /** `grid.bottom` — x-axis label band plus the whole legend block. */
  gridBottomPx: number;
  /** Canvas height. Grows one row height per legend row beyond the first. */
  chartHeightPx: number;
}

/**
 * Canvas-backed measurer. Accurate for the font ECharts will actually draw with, which is
 * what makes the reserved height match the rendered legend.
 */
export function createCanvasTextMeasurer(
  geometry: LegendGeometry = DEFAULT_LEGEND_GEOMETRY,
): TextMeasurer {
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  const ctx = canvas ? canvas.getContext('2d') : null;
  if (!ctx) {
    return createApproximateTextMeasurer(geometry);
  }
  ctx.font = `${geometry.fontSizePx}px ${geometry.fontFamily}`;
  return (text: string) => ctx.measureText(text).width;
}

/**
 * Deterministic fallback for environments without a 2d context. Intentionally crude — it
 * only has to keep the layout sane until a real measurement is available.
 */
export function createApproximateTextMeasurer(
  geometry: LegendGeometry = DEFAULT_LEGEND_GEOMETRY,
): TextMeasurer {
  return (text: string) => Array.from(text ?? '').length * geometry.fontSizePx * 0.55;
}

/**
 * Shorten `label` until it fits `maxWidthPx`, appending the ellipsis.
 *
 * Splits on code points (`Array.from`) rather than UTF-16 units so surrogate pairs and
 * combining marks are never cut in half — Hebrew public names and emoji stay well-formed.
 * Deterministic: the same label and width always yield the same string.
 */
export function truncateToWidth(
  label: string,
  maxWidthPx: number,
  measure: TextMeasurer,
  geometry: LegendGeometry = DEFAULT_LEGEND_GEOMETRY,
): string {
  const text = label ?? '';
  if (maxWidthPx <= 0 || measure(text) <= maxWidthPx) {
    return text;
  }

  const chars = Array.from(text);
  const {ellipsis} = geometry;

  // Longest prefix whose rendered width, with the ellipsis, still fits.
  let low = 0;
  let high = chars.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (measure(chars.slice(0, mid).join('') + ellipsis) <= maxWidthPx) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }

  // Even a bare ellipsis can overflow a pathologically narrow width; keep one character
  // rather than emitting an empty, unidentifiable legend entry.
  return low === 0 ? chars.slice(0, 1).join('') : chars.slice(0, low).join('') + ellipsis;
}

/**
 * Pack legend entries into wrapped rows and derive the resulting chart geometry.
 *
 * Rows are packed greedily from the *displayed* (post-truncation) label widths — measuring
 * the pre-truncation label would over-estimate the row count and reserve dead whitespace.
 *
 * There is deliberately no maximum row count: a cap would let entries fall out of view,
 * which is exactly what #28 removes. Bounding the number of series belongs to #26.
 */
export function computeTimeSeriesChartLayout(
  seriesNames: string[],
  containerWidthPx: number,
  measure: TextMeasurer,
  geometry: LegendGeometry = DEFAULT_LEGEND_GEOMETRY,
  chartGeometry: ChartGeometry = DEFAULT_CHART_GEOMETRY,
): TimeSeriesChartLayout {
  const names = Array.isArray(seriesNames) ? seriesNames : [];

  const legendWidthPx = Math.max(
    chartGeometry.minLegendWidthPx,
    Math.floor((containerWidthPx || 0) - geometry.sidePaddingPx * 2),
  );
  const maxLabelWidthPx = legendWidthPx * geometry.maxLabelFractionOfWidth;

  const displayLabels: string[] = [];
  const displayByName: Record<string, string> = {};
  names.forEach(name => {
    const display = truncateToWidth(name, maxLabelWidthPx, measure, geometry);
    displayLabels.push(display);
    displayByName[name] = display;
  });

  const itemWidth = (label: string) =>
    geometry.iconWidthPx + geometry.iconTextGapPx + measure(label);

  const rows: string[][] = [];
  let current: string[] = [];
  let currentWidthPx = 0;

  displayLabels.forEach(label => {
    const width = itemWidth(label);
    if (current.length === 0) {
      current = [label];
      currentWidthPx = width;
      return;
    }
    const widthWithItem = currentWidthPx + geometry.itemGapPx + width;
    if (widthWithItem > legendWidthPx) {
      rows.push(current);
      current = [label];
      currentWidthPx = width;
    } else {
      current.push(label);
      currentWidthPx = widthWithItem;
    }
  });
  if (current.length > 0) {
    rows.push(current);
  }

  // A chart always carries at least one legend row's worth of reservation, so an empty or
  // single-entry legend still lands on exactly the previous baseline layout.
  const rowCount = Math.max(1, rows.length);
  const legendHeightPx = rowCount * geometry.rowHeightPx;

  return {
    displayLabels,
    displayByName,
    rows,
    rowCount,
    legendWidthPx,
    legendHeightPx,
    gridBottomPx: chartGeometry.xAxisLabelBandPx + legendHeightPx,
    chartHeightPx:
      chartGeometry.baseChartHeightPx + (rowCount - 1) * geometry.rowHeightPx,
  };
}
